/**
 * Instanced debris. One InstancedMesh, capacity = the tier's maxDebris.
 *
 * Each instance gets a random rotation and scale once at build time. Per
 * frame only the translation slots (12, 13, 14) of each 4x4 matrix are
 * rewritten from the tracer buffers, interpolated by alpha. No Matrix4 or
 * Vector3 is created per frame.
 *
 * Rebuilt only on tier change, which is rare and outside the hot path.
 *
 * GPU mode (phase 4): positions live in storage buffers written by the
 * compute kernels in physics/gpu/gpuTracers.ts. The instance matrices keep
 * only rotation and scale; positionNode adds the translation after
 * instancing (NodeMaterial applies instanceMatrix, then positionNode), read
 * straight from the buffers and interpolated by alpha. Nothing is written
 * from the CPU per frame.
 */
import {
  InstancedMesh,
  Matrix4,
  MeshStandardNodeMaterial,
  Quaternion,
  TetrahedronGeometry,
  Vector3,
} from 'three/webgpu';
import { color, hash, instanceIndex, mix, positionLocal, uniform, vec3 } from 'three/tsl';
import type { StorageBufferNode } from 'three/webgpu';
import { RENDER_UNITS_PER_M } from '../config/constants';
import { Rng } from '../core/rng';
import type { Tracers } from '../physics/tracers';

/** Debris piece size, km. Exaggerated so it reads at storm scale. */
const DEBRIS_SIZE_KM = 0.18;

export interface GpuDebrisSource {
  posNode: StorageBufferNode<'vec4'>;
  prevNode: StorageBufferNode<'vec4'>;
}

export interface DebrisHandle {
  readonly mesh: InstancedMesh;
  readonly capacity: number;
  /** GPU mode only: set interpolation alpha, vertical stretch, and instance count. */
  setGpuFrame(alpha: number, yScale: number, count: number): void;
  /** yScale is the display-only vertical stretch. */
  write(tracers: Tracers, alpha: number, yScale: number): void;
  dispose(): void;
}

export function createDebris(capacity: number, seed: number, gpu?: GpuDebrisSource): DebrisHandle {
  const geometry = new TetrahedronGeometry(DEBRIS_SIZE_KM, 0);
  const material = new MeshStandardNodeMaterial({ roughness: 0.8 });
  // TSL: per-instance color from a hash of the instance index. Runs on both backends.
  material.colorNode = mix(color(0x6b4f2a), color(0xa8a29e), hash(instanceIndex));

  const uAlpha = uniform(0);
  const uYScale = uniform(1);
  if (gpu) {
    const cur = gpu.posNode.toAttribute().xyz;
    const prev = gpu.prevNode.toAttribute().xyz;
    const p = mix(prev, cur, uAlpha);
    const k = RENDER_UNITS_PER_M;
    material.positionNode = positionLocal.add(
      vec3(p.x.mul(k), p.y.mul(uYScale.mul(k)), p.z.mul(k)),
    );
  }

  const mesh = new InstancedMesh(geometry, material, capacity);
  mesh.frustumCulled = false;
  mesh.count = 0;

  const rng = new Rng(seed);
  const m = new Matrix4();
  const q = new Quaternion();
  const axis = new Vector3();
  const pos = new Vector3();
  const scl = new Vector3();
  for (let i = 0; i < capacity; i++) {
    axis.set(rng.normal(), rng.normal(), rng.normal()).normalize();
    q.setFromAxisAngle(axis, rng.range(0, Math.PI * 2));
    const s = rng.range(0.5, 1.5);
    scl.set(s, s * rng.range(0.4, 1), s);
    m.compose(pos, q, scl);
    mesh.setMatrixAt(i, m);
  }
  mesh.instanceMatrix.needsUpdate = true;

  const arr = mesh.instanceMatrix.array as Float32Array;
  const k = RENDER_UNITS_PER_M;

  return {
    mesh,
    capacity,
    setGpuFrame(alpha: number, yScale: number, count: number): void {
      uAlpha.value = alpha;
      uYScale.value = yScale;
      mesh.count = count < capacity ? count : capacity;
    },
    write(tracers: Tracers, alpha: number, yScale: number): void {
      const ky = k * yScale;
      const n = tracers.count < capacity ? tracers.count : capacity;
      const { posX, posY, posZ, prevX, prevY, prevZ } = tracers;
      const b = 1 - alpha;
      for (let i = 0; i < n; i++) {
        const o = i * 16;
        arr[o + 12] = ((prevX[i] as number) * b + (posX[i] as number) * alpha) * k;
        arr[o + 13] = ((prevY[i] as number) * b + (posY[i] as number) * alpha) * ky;
        arr[o + 14] = ((prevZ[i] as number) * b + (posZ[i] as number) * alpha) * k;
      }
      mesh.count = n;
      mesh.instanceMatrix.needsUpdate = true;
    },
    dispose(): void {
      geometry.dispose();
      material.dispose();
      mesh.dispose();
    },
  };
}
